import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { MapPin, Mail, Phone, Send } from 'lucide-react';
import { motion } from 'framer-motion';

const images = [
  {
    src: "https://www.helsinkidc.com/images/team/1.jpg",
    title: "Co-location space",
    desc: "Modern co-location space for your infrastructure."
  },
  {
    src: "https://www.helsinkidc.com/images/team/2.jpg",
    title: "Co-location cabinets",
    desc: "Secure cabinets 10U-42U"
  },
  {
    src: "https://www.helsinkidc.com/images/team/3.jpg",
    title: "Virtual Data Center",
    desc: "IAAS and Disaster Recovery solutions."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/2_1.jpg",
    title: "Fire Protection",
    desc: "VESDA systems and Inergen gas suppression protecting data center areas."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/1_2.jpg",
    title: "Capacity",
    desc: "Scalable data center space and rack capacity."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/1_3.jpg",
    title: "Cooling",
    desc: "Efficient air conditioning utilizing Finnish climate."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/2_4.jpg",
    title: "Power",
    desc: "Redundant UPS systems with high energy efficiency."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/1.jpg",
    title: "Redundancy",
    desc: "A-feed and B-feed power to all rack cabinets."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/2_2.jpg",
    title: "Security",
    desc: "24/7 guarding and multi-layer security systems."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/2_3.jpg",
    title: "Backup Power",
    desc: "Diesel generators ensure power during national grid outages."
  },
  {
    src: "https://www.helsinkidc.com/images/portfolio/mixed/1_4.jpg",
    title: "CRAC Units",
    desc: "Redundant cooling with low and high-density zones."
  }
];


export const English: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://portal.helsinkidc.com/api/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormData({ name: '', email: '', subject: '', message: '' });
        alert('Kiitos viestistäsi! Palaamme asiaan mahdollisimman pian.');
      } else {
        alert('Viestin lähetys epäonnistui. Yritä myöhemmin uudelleen.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Viestin lähetys epäonnistui. Yritä myöhemmin uudelleen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Layout>
      <div className="pt-20 min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-slate-900 text-white py-20">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Helsinki DC - Data Center in Helsinki</h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
Would you like more information about our products or services?
Get in touch and we'll get back to you quickly!            </p>
          </div>
        </div>

      {/* Fact Sheet Section */}
      <section className="py-16 bg-slate-50">

        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Technical Specifications</h2>
            <div className="prose prose-slate max-w-none text-slate-600 space-y-4">
              <p>
Helsinki Datacenter (HelsinkiDC.com) offers colocation rack cabinets, dedicated servers, cloud servers and state-of-the-art, ultra-fast network connectivity with excellent Nordic peering and low-latency connections across Europe.              </p>
              <p>
                All core components on the site are designed to provide multiple levels of redundancy and are monitored 24/7 basis. Datacenter backup systems are tested monthly to ensure stability in case of emergency.
              </p>
              <p>
                Helsinki DC provides low density and high density zones and high power usage effectiveness with cold & hot aisle configuration. Power and cooling systems are designed to provide energy efficiency utilizing optimal Finnish climate.
              </p>
 
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Power Supply */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">⚡</span>
                Power supply systems
              </h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> UPS: redundant (N+1)</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> UPS batteries: redundant (N+1)</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Rack power feeds: A+B redundant (2N)</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Main switches: 2N</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Switchboards: redundant (2N)</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Transformers: 2N</li>
              </ul>
            </div>

            {/* Air Conditioning */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center text-cyan-600">❄️</span>
                Air conditioning systems
              </h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2"><span className="text-cyan-500 mt-1">•</span> Chillers: redundant (2N)</li>
                <li className="flex items-start gap-2"><span className="text-cyan-500 mt-1">•</span> Condenser: redundant (2N)</li>
                <li className="flex items-start gap-2"><span className="text-cyan-500 mt-1">•</span> Computer Room Air Conditioning units: redundant (N+1)</li>
                <li className="flex items-start gap-2"><span className="text-cyan-500 mt-1">•</span> Cold aisle / hot aisle configuration</li>
                <li className="flex items-start gap-2"><span className="text-cyan-500 mt-1">•</span> Energy efficient low-density- and high-density-zones available</li>
              </ul>
            </div>

            {/* Fire Detection */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-red-600">🔥</span>
                Fire detection & suppression
              </h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2"><span className="text-red-500 mt-1">•</span> High sensitivity VESDA smoke detection system on all data center areas</li>
                <li className="flex items-start gap-2"><span className="text-red-500 mt-1">•</span> Integrated smoke / heat / fire detection system</li>
                <li className="flex items-start gap-2"><span className="text-red-500 mt-1">•</span> Inergen-based automatic gas suppression system</li>
              </ul>
            </div>

            {/* General Features */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">🛡️</span>
                General Features
              </h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> Access to the data center 24/7</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> Raised floor</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> 24 / 7 NOC and monitoring</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> UPS backup systems and diesel generators</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> BGP routing</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> FICIX internet exchange connections</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> IP addresses (IPv4, IPv6) to suit your needs</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-1">•</span> DNS management with PTR pointer</li>

              </ul>
            </div>
          </div>
        </div>
      </section>


      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {images.map((img, idx) => (
              <motion.div
                key={idx}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-slate-100 group hover:-translate-y-1"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={img.src} 
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{img.title}</h3>
                  <p className="text-slate-600">{img.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>


        <div className="container mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
                <p className="text-slate-600 mb-8">
                  We’re genuinely glad to assist you! 🙌
Please feel free to request our optimal / best-suited data center solution offer – we're ready to prepare it for you.
You may contact us using the form below or directly by email or phone.                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg text-blue-600">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Address</h3>
                    <p className="text-slate-600">Hiomotie 32</p>
                    <p className="text-slate-600">00380 Helsinki, Finland</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg text-blue-600">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                    <p className="text-slate-600">sales@helsinkidc.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg text-blue-600">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Phone</h3>
                    <p className="text-slate-600">+358 10 8433 700</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact us for more information</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                    placeholder="Etunimi Sukunimi"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                    placeholder="posti@esimerkki.fi"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                    placeholder="Viestin aihe"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all resize-none"
                    placeholder="Kirjoita viestisi tähän..."
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Lähetetään...' : 'Send message'}
                  {!isSubmitting && <Send className="w-4 h-4" />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
